import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
  useMediaQuery,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";
import UserList from "./UserList";
import UserAvatar from "./UserAvatar";
import useAuth from "hooks/useAuth";
import { appDrawerWidth as drawerWidth } from "store/constant";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import formatName from "reuseableTable/naming-convention";
import { gradientDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import axios from "axios";
import useRequestIdGenerater from "customhooks";
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp";
import GroupConnectionList from "./GroupConnectionList";

const ChatDrawer = ({
  handleDrawerOpen,
  openChatDrawer,
  setUser,
  users,
  generateUniqueId,
  setRoomId,
  setOpenChatDrawer,
  handleRoomChange,
  getContactList,
  collapsed,
  setCollapsed
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));
  const [status, setStatus] = useState("available");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const drawerBG = theme.palette.mode === "dark" ? "dark.main" : "grey.50";

  const RequestId = useRequestIdGenerater();
  const { rendomRequestId, generateTimeStamp } = useTimeStamp();

  const [connectionlist, setConnectionList] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [pageCache, setPageCache] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);



  const handleGroupCreate = async () => {
    const payload = {
      name: groupName,
      alumniMemberIds: selectedUsers
    }
    try {
      await axios.post(`/v1/communication/group/create-alumni`, payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem(
            "serviceToken"
          )}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
          ),
        },
      });
      setGroupModalOpen(false);
      setGroupName("");
      setSelectedUsers([]);
      getContactList()
      generateTimeStamp();
    } catch (error) {
      generateTimeStamp();
      console.error("Failed to fetch data:", error);
    }

  };
  const getConnectionList = async (currentPage = page, size = rowsPerPage) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `/v1/connection/list?pageSize=${size}&pageNumber=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("serviceToken")}`,
            "Content-Type": "application/json",
            requestId: RequestId(
              Math.floor(1000 + Math.random() * 9999),
              Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
            ),
          },
        }
      );
      const data = response?.data?.data?.content || [];
      setConnectionList(data);
      setAllConnections(data);
      setTotalCount(response?.data?.data?.totalElements);
      setPageCache((prev) => ({
        ...prev,
        [currentPage]: data,
      }));
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setIsLoading(false);
      generateTimeStamp();
    }
  };


  useEffect(() => {
    // if (!search.trim()) {
    if (pageCache[page]) {
      setConnectionList(pageCache[page]);
      setAllConnections(pageCache[page]);
    } else {
      getConnectionList();
    }
    // }
  }, [page, rowsPerPage]);


  return (
    <Drawer
      sx={{
        width: collapsed ? 80 : drawerWidth,
        flexShrink: 0,
        zIndex: { xs: 1100, lg: 0 },
        "& .MuiDrawer-paper": {
          height: "100%",
          width: collapsed ? 80 : drawerWidth,
          boxSizing: "border-box",
          position: "relative",
          border: "none",
          borderRadius: matchDownLG ? "none" : "12px",
          transition: "width 0.3s ease",
          overflowX: "hidden",
          backgroundColor: drawerBG,
        },
      }}
      variant={matchDownLG ? "temporary" : "persistent"}
      anchor="left"
      open={openChatDrawer}
      ModalProps={{ keepMounted: true }}
      onClose={handleDrawerOpen}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {!collapsed && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Typography variant="h4" fontWeight="bold">
              Chat
            </Typography>
          </Box>
        )}

        <Box sx={{ px: collapsed ? 1 : 3, pt: 2, pb: 2 }}>
          {!collapsed ? (
            <Grid container spacing={2} alignItems="center" justifyContent="space-between" wrap="nowrap">
              <Grid item>
                <UserAvatar
                  user={{
                    online_status: status,
                    avatar: `data:image/*;base64, ${user?.image?.data}`,
                    name: formatName(user?.name),
                  }}
                />
              </Grid>
              <Grid item xs zeroMinWidth>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h5" noWrap>
                    {formatName(user?.name)}
                  </Typography>
                  <IconButton onClick={() => setCollapsed((prev) => !prev)} size="small" sx={{ ml: 1 }}>
                    <ChevronLeftIcon />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={1} direction="column" alignItems="center">
              <Grid item>
                <IconButton onClick={() => setCollapsed((prev) => !prev)} size="small">
                  <ChevronRightIcon />
                </IconButton>
              </Grid>
              <Grid item>
                <UserAvatar
                  user={{
                    online_status: status,
                    avatar: `data:image/*;base64, ${user?.image?.data}`,
                    name: formatName(user?.name),
                  }}
                />
              </Grid>
            </Grid>
          )}
          <Divider sx={{ mt: theme.spacing(2) }} />
        </Box>

        {!collapsed && (
          <Box sx={{ px: 1, pb: 1 }}>
            <OutlinedInput
              fullWidth
              id="input-search-header"
              placeholder="Search Contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchTwoToneIcon fontSize="small" />
                </InputAdornment>
              }
              sx={{ borderRadius: "8px", mb: 1 }}
            />
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={() => setGroupModalOpen(true)}
            // disabled={selectedUsers.length < 2}
            >
              Create Group
            </Button>
          </Box>
        )}

        <Box sx={{ flexGrow: 1, height: 200 }}>
          <PerfectScrollbar
            style={{
              overflowX: "hidden",
              height: "100%",
              padding: collapsed ? "8px" : "24px",
              paddingTop: 0,
            }}
          >
            <UserList
              handleRoomChange={handleRoomChange}
              setRoomId={setRoomId}
              setUser={setUser}
              users={users}
              setOpenChatDrawer={setOpenChatDrawer}
              generateUniqueId={generateUniqueId}
              collapsed={collapsed}
              searchTerm={searchTerm}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
            />
          </PerfectScrollbar>
        </Box>
      </Box>

      {/* Group Modal */}
      {groupModalOpen && (
        <GroupConnectionList
          users={connectionlist}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          fetchMoreData={getConnectionList}
          hasMore={totalCount > connectionlist.length}
          loading={isLoading}
          searchTerm={searchTerm}
          groupName={groupName}
          setGroupName={setGroupName}
          groupModalOpen={groupModalOpen}
          setGroupModalOpen={setGroupModalOpen}
          handleGroupCreate={handleGroupCreate}
        />
      )}

    </Drawer>
  );
};

ChatDrawer.propTypes = {
  handleDrawerOpen: PropTypes.func,
  openChatDrawer: PropTypes.bool,
  setUser: PropTypes.func,
  users: PropTypes.array,
  generateUniqueId: PropTypes.func,
  setRoomId: PropTypes.func,
  CheckdrawrWidth: PropTypes.any,
  setOpenChatDrawer: PropTypes.func,
  handleRoomChange: PropTypes.func,
};

export default ChatDrawer;
